/**
 * StoreKit Direct — thin JS shim over the native Capacitor plugin
 * defined in `/app/ios_native_storekit/StoreKitDirectPlugin.swift`.
 *
 * T43 — `ping()` is the canonical runtime availability probe;
 * `Capacitor.isPluginAvailable()` is unreliable on Cap 8.
 *
 * T44 — Capacitor `CAPPluginCall.reject(code, msg, error, data)` puts
 * the extra `data` dictionary on the resulting error object's `.data`
 * field. We expose it (and any extra keys the bridge added) so the
 * paywall can show productId / requestedIds / returnedCount / etc. in
 * its diagnostic banner without needing a second round-trip.
 */

import { registerPlugin } from "@capacitor/core";

const StoreKitDirect = registerPlugin("StoreKitDirect");
export default StoreKitDirect;

/**
 * Normalise a Capacitor plugin rejection into an Error with predictable
 * `.code`, `.message`, and `.data` fields. Preserves any extra
 * diagnostic fields the Swift side attached so the UI can render the
 * full reason without us inventing fallback copy.
 */
function _normaliseRejection(e, fallbackCode) {
  const code = e?.code || fallbackCode;
  const message = e?.message || e?.errorMessage || code;
  const err = new Error(message);
  err.code = code;
  // Capacitor places reject(code, msg, error, data) → data on the
  // rejection object. Different Cap versions expose it under
  // `.data`, `.errorData`, or as top-level extra keys. We unify.
  err.data =
    e?.data ||
    e?.errorData ||
    // strip the well-known fields and treat the rest as diagnostic data
    (e && typeof e === "object"
      ? Object.fromEntries(
          Object.entries(e).filter(
            ([k]) => !["code", "message", "errorMessage", "stack"].includes(k),
          ),
        )
      : {});
  return err;
}

export async function ping() {
  try {
    return await StoreKitDirect.ping();
  } catch (e) {
    throw _normaliseRejection(e, "PING_FAILED");
  }
}

export async function getProduct(productId) {
  try {
    return await StoreKitDirect.getProduct({ productId });
  } catch (e) {
    throw _normaliseRejection(e, "FETCH_FAILED");
  }
}

export async function purchase(productId) {
  try {
    return await StoreKitDirect.purchase({ productId });
  } catch (e) {
    throw _normaliseRejection(e, "PURCHASE_FAILED");
  }
}

export async function restore() {
  try {
    return await StoreKitDirect.restore();
  } catch (e) {
    throw _normaliseRejection(e, "RESTORE_FAILED");
  }
}

/**
 * T47 — Silent entitlement probe. Walks `Transaction.currentEntitlements`
 * on the native side without calling `AppStore.sync()` (no Apple ID
 * password prompt). Used by the app's auto-sync on sign-in success and
 * on app resume to bring the backend in line with the device's actual
 * receipt — so a signed-in user who already purchased on this Apple ID
 * sees "Pro" in Settings immediately without ever tapping Restore.
 */
export async function getEntitlements() {
  try {
    return await StoreKitDirect.getEntitlements();
  } catch (e) {
    throw _normaliseRejection(e, "GET_ENTITLEMENTS_FAILED");
  }
}
